import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import {
  ShieldCheck, SpinnerGap, ArrowClockwise, ArrowsClockwise, VideoCamera,
  CheckCircle, Warning, XCircle, Gauge
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { useLang } from "../contexts/LanguageContext";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";

import { API_BASE as API } from "../lib/apiBase";
import { apiErrorMessage } from "../lib/apiError";

export default function ComplianceScoringPage() {
  const { user } = useAuth();
  const { lang, t, tr } = useLang();
  const [videos, setVideos] = useState([]);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scoring, setScoring] = useState(null);
  // Vincent 2026-05-06 (bug #68) — accordéon ouvert/fermé par vidéo
  const [expandedVideoId, setExpandedVideoId] = useState(null);
  // Vincent #400 (Eric 2026-07-01 SPEC STRICK) — filtre parcours + tri 6 critères.
  const [pathways, setPathways] = useState([]);
  const [pathwayFilter, setPathwayFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name_asc");
  // Tâche 1275 — Progression de l'analyse en lot (null = aucun lot en cours).
  const [batchProgress, setBatchProgress] = useState(null);

  useEffect(() => { loadData(); }, []);

  /**
   * Tâche 1272 — `/api/videos` renvoie 50 fiches par page, les plus récentes
   * d'abord. La page n'en demandait qu'une : tout ce qui était plus ancien
   * n'arrivait jamais jusqu'ici, d'où l'impression d'avoir « perdu » les
   * vidéos et leurs scores AMF avant une certaine date. On parcourt donc
   * toutes les pages.
   */
  const fetchAllVideos = async () => {
    const PAGE = 500;
    const all = [];
    for (let page = 1; page <= 40; page += 1) {
      const { data } = await axios.get(
        `${API}/api/videos?page=${page}&limit=${PAGE}`,
        { withCredentials: true },
      );
      const items = data.items || data || [];
      all.push(...items);
      if (items.length < PAGE) break;
    }
    return all;
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [vRes, sRes, pRes] = await Promise.all([
        fetchAllVideos(),
        axios.get(`${API}/api/compliance/scores`, { withCredentials: true }),
        axios.get(`${API}/api/pathways`, { withCredentials: true }).catch(() => ({ data: { pathways: [] } })),
      ]);
      // Tâche 1272 (Eric 2026-06-29) — On excluait tout ce qui n'avait pas
      // `pipeline_status === "complete"` : les fiches d'avant ce champ
      // disparaissaient de la page (et leurs scores AMF semblaient perdus,
      // alors qu'ils sont toujours en base). On n'écarte plus que ce qui est
      // réellement inanalysable : un document texte, un échec, une annulation.
      setVideos(vRes.filter(v => !v.is_text_doc
        && !["failed", "cancelled"].includes(v.pipeline_status)));
      setScores(sRes.data.scores || []);
      const pw = pRes.data?.pathways || pRes.data || [];
      setPathways(Array.isArray(pw) ? pw : []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const scoreVideo = async (videoId) => {
    setScoring(videoId);
    try {
      const { data } = await axios.post(
        `${API}/api/compliance/score-video/${videoId}`,
        // Tâche 1383 — L'IA rédige ses constats dans la langue de l'écran :
        // ils sont ENREGISTRÉS, donc intraduisibles après coup.
        { lang },
        { withCredentials: true },
      );
      await loadData();
      const verdict = data?.result?.verdict || data?.verdict;
      const score = data?.result?.score_global ?? data?.score_global;
      const previous = data?.previous_score;
      const delta = data?.delta;
      if (verdict !== undefined && score !== undefined) {
        const verdictLabel = ({
          conforme: tr("✅ Conforme", "✅ Compliant"),
          attention: tr("⚠️ Attention", "⚠️ Caution"),
          non_conforme: tr("❌ Non conforme", "❌ Non-compliant"),
        })[verdict] || verdict;
        // Vincent #137 — Toast enrichi avec comparaison vs précédent.
        let line2 = "";
        if (previous != null && delta != null) {
          const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "=";
          const sign = delta > 0 ? "+" : "";
          line2 = tr(
            ` (préc. ${previous}/10 → ${sign}${delta} ${arrow})`,
            ` (prev. ${previous}/10 → ${sign}${delta} ${arrow})`,
          );
        }
        toast.success(`${verdictLabel} — ${tr("Score", "Score")} ${score}/10${line2}`, { duration: 9000 });
      } else {
        toast.success(tr("Analyse AMF terminée. Cliquez sur ▶ pour voir les détails.", "AMF analysis complete. Click ▶ to see the details."));
      }
    } catch (err) {
      console.error(err);
      // Vincent bug #124 (Eric 2026-05-14) — Use the centralised helper so
      // FastAPI validation errors (list of {loc,msg,type}) no longer surface
      // as "[object Object]" in the toast.
      toast.error(`${tr("Erreur d'analyse : ", "Analysis error: ")}${apiErrorMessage(err, tr("erreur inconnue", "unknown error"))}`, { duration: 9000 });
    }
    setScoring(null);
  };

  const getScoreForVideo = (videoId) => scores.find(s => s.video_id === videoId);

  /**
   * Tâche 1275 (Eric 2026-06-29) — Analyser en lot ce qui n'a jamais été noté.
   * Il fallait cliquer « Analyser » vidéo par vidéo : sur cent fiches,
   * personne ne le fait. Les appels restent SÉQUENTIELS (chaque analyse est un
   * appel IA facturé) et la progression est affichée, pour qu'on puisse
   * fermer la page en sachant où ça en est.
   */
  const scoreAllUnscored = async () => {
    const pending = videos.filter(v => !getScoreForVideo(v.id));
    if (pending.length === 0) {
      toast.info(tr("Toutes les vidéos ont déjà un score AMF.", "Every video already has an AMF score."));
      return;
    }
    const ok = await confirmDialog(
      tr(
        `Lancer l'analyse AMF sur ${pending.length} vidéo(s) non notée(s) ? Chaque analyse est un appel IA facturé.`,
        `Run the AMF analysis on ${pending.length} unscored video(s)? Each analysis is a billed AI call.`,
      ),
      { tone: "warning", confirmLabel: tr("Analyser", "Analyse") },
    );
    if (!ok) return;
    setBatchProgress({ done: 0, total: pending.length, failed: 0 });
    let done = 0, failed = 0;
    for (const v of pending) {
      try {
        await axios.post(`${API}/api/compliance/score-video/${v.id}`, { lang }, { withCredentials: true });
      } catch {
        failed += 1;
      }
      done += 1;
      setBatchProgress({ done, total: pending.length, failed });
    }
    setBatchProgress(null);
    await loadData();
    if (failed === 0) toast.success(tr(`${done} vidéo(s) analysée(s).`, `${done} video(s) analysed.`));
    else toast.warning(tr(
      `${done - failed} analysée(s), ${failed} en échec — relance pour compléter.`,
      `${done - failed} analysed, ${failed} failed — run again to finish.`,
    ));
  };

  // Vincent bug #90 (2026-05-08) — Palette colorblind-friendly :
  //  Conforme = bleu (au lieu de vert) car le bleu est distinguable du rouge même pour les daltoniens.
  //  Les icônes (CheckCircle/Warning/XCircle) renforcent la différenciation au-delà de la couleur.
  const verdictConfig = {
    conforme: { icon: CheckCircle, color: "text-blue-500", bg: "bg-blue-500/10 border-blue-500/30", label: tr("Conforme", "Compliant") },
    attention: { icon: Warning, color: "text-amber-500", bg: "bg-amber-500/10 border-amber-500/30", label: tr("Attention", "Caution") },
    non_conforme: { icon: XCircle, color: "text-rose-600", bg: "bg-rose-600/10 border-rose-600/30", label: tr("Non conforme", "Non-compliant") },
  };

  // Tâche 1414 (Eric 2026-09-30) — Le niveau de risque arrive du serveur en
  // clés françaises SANS accent (`eleve`, `moyen`, `faible`) : ce sont des
  // valeurs stockées, pas du texte d'interface. On les affiche traduites.
  const niveauLabel = (niveau) => ({
    eleve: tr("élevé", "high"),
    moyen: tr("moyen", "medium"),
    faible: tr("faible", "low"),
  })[niveau] || niveau;

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={32} className="animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="compliance-scoring-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-500/20 to-cyan-500/20 border border-teal-500/20 flex items-center justify-center">
            <Gauge size={24} className="text-teal-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold font-['Outfit']" data-testid="compliance-title">{t("scoring_title")}</h1>
            <p className="text-xs text-muted-foreground">{t("scoring_subtitle")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Tâche 1275 — Placé à gauche du bouton de rafraîchissement. */}
          <Button
            variant="default"
            size="sm"
            onClick={scoreAllUnscored}
            disabled={!!batchProgress || !!scoring}
            className="gap-1.5"
            data-testid="compliance-score-all-btn"
            title={tr("Analyser toutes les vidéos qui n'ont pas encore de score AMF", "Analyse every video that has no AMF score yet")}
          >
            {batchProgress
              ? <><SpinnerGap size={14} className="animate-spin" /> {batchProgress.done}/{batchProgress.total}</>
              : <><Gauge size={14} /> {tr(`Analyser en lot (${videos.filter(v => !getScoreForVideo(v.id)).length})`, `Batch analysis (${videos.filter(v => !getScoreForVideo(v.id)).length})`)}</>}
          </Button>
          <Button variant="outline" size="sm" onClick={loadData} className="gap-1.5"><ArrowClockwise size={14} /> {tr("Rafraîchir", "Refresh")}</Button>
        </div>
      </div>

      {/* Vincent bug #77 (2026-05-07) — accordion d'explication des scores AMF.
          Permet aux conseillers de comprendre comment les scores sont calculés
          AVANT de lancer une analyse, et de débriefer les verdicts a posteriori. */}
      <details className="bg-card border border-border rounded-xl overflow-hidden" data-testid="amf-scoring-explainer">
        <summary className="cursor-pointer px-5 py-3 hover:bg-secondary/30 flex items-center justify-between text-sm font-medium select-none">
          <span className="flex items-center gap-2">
            <span className="text-base">ℹ️</span>
            {lang === "fr"
              ? "Comment les scores AMF sont-ils calculés ?"
              : "How are AMF scores calculated?"}
          </span>
          <span className="text-xs text-muted-foreground">{lang === "fr" ? "Cliquer pour ouvrir" : "Click to open"}</span>
        </summary>
        <div className="px-5 pb-5 pt-2 text-sm text-muted-foreground space-y-3 border-t border-border">
          <p>
            {lang === "fr"
              ? "Chaque vidéo est analysée par GPT-5.2 sur la base des règles AMF (Autorité des marchés financiers) qui régissent les conseillers en sécurité financière au Québec."
              : "Each video is analyzed by GPT-5.2 against the AMF (Autorité des marchés financiers) rules for Québec financial advisors."}
          </p>
          <div>
            <p className="font-semibold text-foreground mb-1.5">{lang === "fr" ? "Verdict global" : "Overall verdict"}</p>
            <ul className="space-y-1 ml-4">
              <li className="flex items-start gap-2"><CheckCircle size={14} weight="fill" className="text-blue-500 mt-0.5 shrink-0" /> <span><strong className="text-blue-700">{tr("Conforme", "Compliant")}</strong> (8-10/10) — {lang === "fr" ? "aucun écart majeur, conforme aux pratiques AMF." : "no major deviations, AMF-compliant."}</span></li>
              <li className="flex items-start gap-2"><Warning size={14} weight="fill" className="text-amber-500 mt-0.5 shrink-0" /> <span><strong className="text-amber-700">{tr("Attention", "Caution")}</strong> (5-7/10) — {lang === "fr" ? "écarts mineurs ou ambiguïtés à clarifier (ex: déclaration de revenus, formulaires)." : "minor gaps or ambiguities to clarify."}</span></li>
              <li className="flex items-start gap-2"><XCircle size={14} weight="fill" className="text-rose-500 mt-0.5 shrink-0" /> <span><strong className="text-rose-700">{tr("Non conforme", "Non-compliant")}</strong> (0-4/10) — {lang === "fr" ? "violations claires (ex: promesse de rendement, vente sans analyse de besoins, conflit d'intérêts non divulgué)." : "clear violations."}</span></li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-foreground mb-1.5">{lang === "fr" ? "Critères évalués" : "Evaluated criteria"}</p>
            <ul className="space-y-0.5 ml-4 text-xs">
              <li>• <strong>{lang === "fr" ? "Analyse de besoins" : "Needs analysis"}</strong> — {lang === "fr" ? "le conseiller a-t-il évalué la situation du client avant de proposer un produit ?" : "did the advisor assess the client's situation first?"}</li>
              <li>• <strong>{lang === "fr" ? "Divulgation de conflit" : "Conflict disclosure"}</strong> — {lang === "fr" ? "rémunération, partenariats, incitatifs déclarés explicitement ?" : "compensation, partnerships, incentives disclosed?"}</li>
              <li>• <strong>{lang === "fr" ? "Promesses de rendement" : "Return promises"}</strong> — {lang === "fr" ? "absence de garantie de performance non documentée." : "no undocumented performance guarantees."}</li>
              <li>• <strong>{lang === "fr" ? "Adéquation produit/profil" : "Product suitability"}</strong> — {lang === "fr" ? "le produit recommandé correspond-il au profil de risque ?" : "does the recommended product match the risk profile?"}</li>
              <li>• <strong>{lang === "fr" ? "Documentation" : "Documentation"}</strong> — {lang === "fr" ? "formulaires, signatures, traces écrites mentionnés." : "forms, signatures, written traces referenced."}</li>
            </ul>
          </div>
          <div className="text-xs italic">
            {lang === "fr"
              ? "💡 Astuce : cliquez sur ▶ à droite d'un verdict pour voir le détail des risques et recommandations identifiés par l'IA."
              : "💡 Tip: click ▶ next to a verdict to see the detailed risks and recommendations identified by AI."}
          </div>
        </div>
      </details>

      {/* Vincent #400 (Eric 2026-07-01 SPEC STRICK) — Filtre parcours + tri */}
      <div className="flex flex-wrap items-center gap-3 text-xs" data-testid="compliance-toolbar">
        <label className="inline-flex items-center gap-2 text-zinc-700 font-medium">
          {lang === "fr" ? "Parcours" : "Pathway"} :
          <select
            value={pathwayFilter}
            onChange={(e) => setPathwayFilter(e.target.value)}
            className="border border-zinc-300 rounded-md px-2 py-1.5 text-xs bg-white max-w-[280px]"
            data-testid="compliance-pathway-filter"
          >
            <option value="all">{lang === "fr" ? "Tous les parcours" : "All pathways"}</option>
            {[...pathways]
              .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "fr", { sensitivity: "base" }))
              .map(p => (
                <option key={p.id} value={p.id}>
                  {p.pathway_type === "onboarding" ? "🎓 " : "📚 "}{p.name}
                </option>
              ))}
          </select>
        </label>
        <label className="inline-flex items-center gap-2 text-zinc-700 font-medium">
          {lang === "fr" ? "Trier par" : "Sort by"} :
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="border border-zinc-300 rounded-md px-2 py-1.5 text-xs bg-white"
            data-testid="compliance-sort"
          >
            <option value="name_asc">{lang === "fr" ? "Ordre alphabétique (A → Z)" : "Alphabetical (A → Z)"}</option>
            <option value="name_desc">{lang === "fr" ? "Ordre alphabétique (Z → A)" : "Alphabetical (Z → A)"}</option>
            <option value="created_asc">{lang === "fr" ? "Date de création (croissante)" : "Created (asc)"}</option>
            <option value="created_desc">{lang === "fr" ? "Date de création (décroissante)" : "Created (desc)"}</option>
            <option value="updated_asc">{lang === "fr" ? "Date de modification (croissante)" : "Modified (asc)"}</option>
            <option value="updated_desc">{lang === "fr" ? "Date de modification (décroissante)" : "Modified (desc)"}</option>
          </select>
        </label>
      </div>

      {/* Videos with scores */}
      <div className="space-y-3" data-testid="video-scores-list">
        {(() => {
          const cmpStr = (a, b) => String(a || "").localeCompare(String(b || ""), "fr", { sensitivity: "base" });
          const ts = (d) => (d ? new Date(d).getTime() : 0);
          let arr = videos;
          if (pathwayFilter !== "all") {
            const p = pathways.find(x => x.id === pathwayFilter);
            const set = new Set(p?.video_ids || []);
            arr = arr.filter(v => set.has(v.id));
          }
          arr = [...arr];
          switch (sortBy) {
            case "name_asc": arr.sort((a, b) => cmpStr(a.title, b.title)); break;
            case "name_desc": arr.sort((a, b) => cmpStr(b.title, a.title)); break;
            case "created_asc": arr.sort((a, b) => ts(a.created_at) - ts(b.created_at)); break;
            case "created_desc": arr.sort((a, b) => ts(b.created_at) - ts(a.created_at)); break;
            case "updated_asc": arr.sort((a, b) => ts(a.updated_at) - ts(b.updated_at)); break;
            case "updated_desc": arr.sort((a, b) => ts(b.updated_at) - ts(a.updated_at)); break;
            default: break;
          }
          return arr.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <VideoCamera size={40} className="mx-auto mb-3 opacity-30" />
              <p>{t("no_videos_to_analyze")}</p>
            </div>
          ) : arr.map(video => {
          const existingScore = getScoreForVideo(video.id);
          const scoreEntry = existingScore; // Vincent #137 — alias pour clarté (contient delta/history/previous_score)
          const result = existingScore?.result;
          const verdict = result ? verdictConfig[result.verdict] || verdictConfig.attention : null;
          const VerdictIcon = verdict?.icon || ShieldCheck;

          return (
            <div key={video.id} className="bg-card border border-border rounded-xl p-5" data-testid={`video-score-${video.id}`}>
              {/* Vincent #88 (2026-05-12 fix) — Ordre gauche→droite imposé :
                  Triangle → Caméra → Nom → Score → Bouton tr("Recalculer", "Recompute").
                  Plus de justify-between : tout sur un flex row gauche-aligné. */}
              <div className="flex items-center gap-3 flex-wrap">
                {/* 1. Triangle accordéon (seulement si déjà scoré) */}
                {result && (
                  <button
                    onClick={() => setExpandedVideoId(expandedVideoId === video.id ? null : video.id)}
                    className="flex-shrink-0 text-sm w-7 h-7 rounded hover:bg-secondary transition-colors flex items-center justify-center"
                    data-testid={`toggle-score-${video.id}`}
                    title={expandedVideoId === video.id ? tr("Fermer l'analyse", "Close the analysis") : tr("Ouvrir l'analyse", "Open the analysis")}
                  >
                    {expandedVideoId === video.id ? "▼" : "▶"}
                  </button>
                )}

                {/* 2. Icône caméra */}
                <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                  <VideoCamera size={18} className="text-muted-foreground" />
                </div>

                {/* 3. Nom de fichier + sous-titre */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm truncate">{video.title}</h3>
                  {/* Tâche 1272 — Les fiches anciennes n'ont pas toujours de
                      date : « Invalid Date » n'apprend rien à personne. */}
                  <p className="text-xs text-muted-foreground truncate">{video.training_theme}{Number.isNaN(new Date(video.created_at).getTime()) ? "" : ` — ${new Date(video.created_at).toLocaleDateString(lang === "en" ? "en-CA" : "fr-FR")}`}</p>
                </div>

                {/* 4. Badge de score (seulement si scoré) */}
                {result && (
                  <div className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${verdict.bg}`} data-testid={`score-badge-${video.id}`}>
                    <VerdictIcon size={14} weight="fill" className={verdict.color} />
                    {verdict.label} — {result.score_global}/10
                  </div>
                )}
                {/* Vincent #137 (Eric 2026-05-13) — Badge delta vs score précédent. */}
                {scoreEntry?.delta != null && scoreEntry?.previous_score != null && (
                  <div
                    className={`flex-shrink-0 px-2 py-1 rounded-md text-[11px] font-bold border ${
                      scoreEntry.delta > 0
                        ? "bg-teal-50 text-teal-700 border-teal-300"
                        : scoreEntry.delta < 0
                        ? "bg-red-50 text-red-700 border-red-300"
                        : "bg-zinc-100 text-zinc-600 border-zinc-300"
                    }`}
                    data-testid={`score-delta-${video.id}`}
                    title={tr(`Score précédent : ${scoreEntry.previous_score}/10`, `Previous score: ${scoreEntry.previous_score}/10`)}
                  >
                    {scoreEntry.delta > 0 ? "▲ +" : scoreEntry.delta < 0 ? "▼ " : "= "}
                    {scoreEntry.delta}
                  </div>
                )}

                {/* 5. Bouton Recalculer / Analyser */}
                {result ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => scoreVideo(video.id)}
                    disabled={scoring === video.id}
                    className="flex-shrink-0 gap-1.5"
                    data-testid={`rescore-btn-${video.id}`}
                    title={tr("Recalculer l'analyse de conformité AMF", "Recompute the AMF compliance analysis")}
                  >
                    {scoring === video.id ? <SpinnerGap size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />}
                    {scoring === video.id ? tr("Analyse...", "Analysing…") : tr("Recalculer", "Recompute")}
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => scoreVideo(video.id)} disabled={scoring === video.id} className="flex-shrink-0 gap-1.5" data-testid={`score-btn-${video.id}`}>
                    {scoring === video.id ? <SpinnerGap size={14} className="animate-spin" /> : <Gauge size={14} />}
                    {scoring === video.id ? tr("Analyse…", "Analysing…") : tr("Analyser", "Analyse")}
                  </Button>
                )}
              </div>

              {/* Score details — Vincent 2026-05-06 (bug #68) : caché si fermé */}
              {result?.risques?.length > 0 && expandedVideoId === video.id && (
                <div className="mt-4 pt-4 border-t border-border space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("risks_detected")}</p>
                  {result.risques.map((r, i) => (
                    <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${
                      r.niveau === "eleve" ? "border-rose-500/20 bg-rose-500/5" :
                      r.niveau === "moyen" ? "border-amber-500/20 bg-amber-500/5" :
                      "border-border bg-secondary/30"
                    }`} data-testid={`risk-${video.id}-${i}`}>
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                        r.niveau === "eleve" ? "bg-rose-500" : r.niveau === "moyen" ? "bg-amber-500" : "bg-teal-500"
                      }`} />
                      <div>
                        <p className="text-sm font-medium">{r.critere}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{r.detail}</p>
                      </div>
                      <span className={`ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${
                        r.niveau === "eleve" ? "bg-rose-500/10 text-rose-400" :
                        r.niveau === "moyen" ? "bg-amber-500/10 text-amber-400" :
                        "bg-teal-500/10 text-teal-400"
                      }`}>{niveauLabel(r.niveau)}</span>
                    </div>
                  ))}
                </div>
              )}

              {result?.recommandations?.length > 0 && expandedVideoId === video.id && (
                <div className="mt-3 space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("recommendations")}</p>
                  {result.recommandations.map((rec, i) => (
                    <p key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                      <CheckCircle size={13} className="text-cyan-400 mt-0.5 flex-shrink-0" /> {rec}
                    </p>
                  ))}
                </div>
              )}

              {/* Vincent #137 (Eric 2026-05-13) — Timeline historique des scores AMF.
                  Permet à Vincent/Eric de voir si une vidéo s'améliore après corrections. */}
              {expandedVideoId === video.id && Array.isArray(scoreEntry?.history) && scoreEntry.history.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border" data-testid={`score-history-${video.id}`}>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                    {tr(
                      `Historique des analyses (${scoreEntry.history.length} précédente${scoreEntry.history.length > 1 ? "s" : ""})`,
                      `Analysis history (${scoreEntry.history.length} previous)`,
                    )}
                  </p>
                  <ul className="space-y-1.5">
                    {/* Run courant en tête */}
                    <li className="flex items-center gap-2 text-xs bg-blue-50 border border-blue-200 rounded px-2 py-1">
                      <span className="text-[10px] uppercase font-bold text-blue-700">{tr("Maintenant", "Now")}</span>
                      <span className="font-mono text-zinc-900">{result.score_global}/10</span>
                      <span className="text-zinc-500">— {verdict?.label}</span>
                      <span className="text-zinc-400 ml-auto">{new Date(scoreEntry.scored_at).toLocaleString(lang === "en" ? "en-CA" : "fr-CA", { dateStyle: "short", timeStyle: "short" })}</span>
                    </li>
                    {/* Anciens runs, plus récent en haut */}
                    {scoreEntry.history.slice().reverse().map((h, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs bg-zinc-50 border border-zinc-200 rounded px-2 py-1">
                        <span className="text-[10px] uppercase font-bold text-zinc-500">−{i + 1}</span>
                        <span className="font-mono text-zinc-700">{h.score_global}/10</span>
                        <span className="text-zinc-500">— {(verdictConfig[h.verdict] || verdictConfig.attention).label}</span>
                        <span className="text-zinc-400 text-[11px]">· {tr(`${h.risques_count} risque${h.risques_count > 1 ? "s" : ""}`, `${h.risques_count} risk${h.risques_count > 1 ? "s" : ""}`)}</span>
                        <span className="text-zinc-400 ml-auto">{h.scored_at ? new Date(h.scored_at).toLocaleString(lang === "en" ? "en-CA" : "fr-CA", { dateStyle: "short", timeStyle: "short" }) : "—"}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        });
        })()}
      </div>
    </div>
  );
}
